import React from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import FormSection from "@ats/src/components/forms/FormSection";
import Button from "@ats/src/components/shared/Button";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import BetaTag from "@ats/src/components/shared/BetaTag";

import { useOrganizationAiCreditBalance } from "@shared/queryHooks/useOrganizationAiCreditBalance";
import AiCreditBalance from "./accountPlatoAi/AiCreditBalance";
import AiMonthlyCreditUsage from "./accountPlatoAi/AiMonthlyCreditUsage";

function OrganizationAiUsage(props: any) {
  const { data: balance, isLoading } = useOrganizationAiCreditBalance();

  const goToBilling = () => {
    props.history.push(props.match.url.replace(/\/usage$/, "/billing"));
  };

  return (
    <>
      <Helmet title="Plato AI usage" />
      <SettingsContainer
        title={
          <>
            Plato AI usage <BetaTag variant="filledDarkOnly" />
          </>
        }
        description="Your organization's current Plato AI credit balance and where it comes from."
        fullWidthForm={true}
      >
        {isLoading || !balance ? (
          <LoadingIndicator label="Loading..." />
        ) : (
          <>
            <FormSection
              title="Credit balance"
              intro="Your credits and where they come from. Credits are spent in order: monthly plan, then subscription add-on, then one-time top-up."
            >
              <AiCreditBalance balance={balance} />

              <Styled.TotalRow>
                <Styled.Total>
                  <span className="value">{balance.totalCreditsRemaining.toLocaleString()}</span>
                  <span className="label">credits total</span>
                </Styled.Total>
                <Button styleType="primary" onClick={goToBilling}>
                  Buy credits
                </Button>
              </Styled.TotalRow>
            </FormSection>

            <FormSection
              title="Credits used per month"
              intro="Credits your team has used on successful Plato reviews, by month. Select a different month to see it against the month before it."
            >
              <AiMonthlyCreditUsage monthlyCreditUsage={balance.monthlyCreditUsage} />
            </FormSection>
          </>
        )}
      </SettingsContainer>
    </>
  );
}

export default OrganizationAiUsage;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.TotalRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: OrganizationAiUsage_TotalRow;
    display: flex;
    flex-direction: column;
    gap: 18px;
    max-width: 696px;

    ${t.mq["md"]} {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    ${[t.mt(5), t.py(4), t.px(4), t.rounded.md]}
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : "transparent"};
  `;
});

Styled.Total = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: OrganizationAiUsage_Total;
    display: flex;
    align-items: baseline;
    gap: ${t.spacing[2]};

    .value {
      ${[t.text.h2]}
      color: ${t.dark ? t.color.gray[100] : t.color.black};
      font-variant-numeric: tabular-nums;
    }
    .label {
      ${[t.text.sm]}
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});
